import { FormGroup } from '@angular/forms';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { addRemoveError, getControl, getFormArray, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { isEqual } from 'lodash-es';
import { INCONGRUENT_NUMBER, REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { formPaths } from '@shared/constants/form-paths.const';
import {
    getSchedaCoopSocialiControl,
    getSchedaCoopSocialiGroup,
    schedaCoopSocialiCkHelper,
} from './helpers/scheda-coop-sociali-form-validators.helper';

export const schedaCoopSocialiCategoriaRules: FormGroupRules = {
    my_SottocategoriaSezEnteSociale: (form: FormGroup) => {
        categoriaHelper(form, 'my_SottocategoriaSezEnteSociale');
    },

    my_CkSociSvantagg: (form: FormGroup) => {
        categoriaHelper(form, 'my_CkSociSvantagg');
    },

    /** InfoPath (errorCondition su PercentPersSvantagg): l'errore dipende da my_PersSvantagg vuoto, NON dal valore della percentuale. */
    my_PercentPersSvantagg: (form: FormGroup) => {
        const control = getSchedaCoopSocialiControl(form, 'my_PercentPersSvantagg');
        const my_PersSvantagg = getSchedaCoopSocialiControl(form, 'my_PersSvantagg').value;
        const errors = control.errors ?? {};

        addRemoveError(isNilOrEmpty(my_PersSvantagg) && hasCategoria(form), errors, 'my_PercentPersSvantagg', { status: REQUIRED_STATUS });

        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_PersSvantagg: (form: FormGroup) => {
        categoriaHelper(form, 'my_PersSvantagg');
    },

    my_NormodotateA: (form: FormGroup) => {
        categoriaHelper(form, 'my_NormodotateA');
    },

    my_NormodotateB: (form: FormGroup) => {
        categoriaHelper(form, 'my_NormodotateB');
    },

    my_TotLavoratori: (form: FormGroup) => {
        const schedaGroup = getSchedaCoopSocialiGroup(form);
        const ebsGroup = getGroup(form, formPaths.baseSociale);
        const control = getControl(schedaGroup, 'my_TotLavoratori');
        const errors = control.errors ?? {};

        const isActive = hasCategoria(form);
        const totLavoratori = toNum(control.value);
        const somma = toNum(getControl(ebsGroup, 'my_UnitaSociUtil').value) + toNum(getControl(ebsGroup, 'my_UnitaNonSociUtil').value);

        addRemoveError(isNilOrEmpty(control.value) && isActive, errors, 'my_TotLavoratoriRequired', {
            status: REQUIRED_STATUS,
            message: 'Specificare le unità normodotate lettera a, le unità normodotate lettera b e il numero di persone svantaggiate.',
        });

        addRemoveError(totLavoratori !== somma && isActive, errors, 'my_TotLavoratoriNonConforme', {
            status: "Valore non conforme con quanto dichiarato nella 'Effettività Base Sociale'",
            message:
                "La somma delle unità normodotate lettera a, unità normodotate lettera b e persone svantaggiate deve coincidere con il totale dei lavoratori (soci più non soci) dichiarato nel par. 'Effettività Base Sociale'",
        });

        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_NCoopConsorzio: (form: FormGroup) => {
        const schedaGroup = getSchedaCoopSocialiGroup(form);
        const control = getControl(schedaGroup, 'my_NCoopConsorzio');
        const my_ScSocObbl: string = getControl(schedaGroup, 'my_ScSocObbl').value;
        const consorzioCond = getControl(schedaGroup, 'my_CkTrattasiDiConsorzio').value === '1';
        const errors = control.errors ?? {};

        addRemoveError(!isNilOrEmpty(control.value) && toNum(control.value) <= 1 && consorzioCond && my_ScSocObbl === '1', errors, 'my_NCoopConsorzio', {
            status: INCONGRUENT_NUMBER,
        });

        addRemoveError(isNilOrEmpty(control.value) && consorzioCond && my_ScSocObbl === '1', errors, 'my_NCoopConsorziRequired', { status: REQUIRED_STATUS });

        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkConsorzConforme: (form: FormGroup) => {
        schedaCoopSocialiCkHelper(form, 'my_CkConsorzConforme', 'my_CkTrattasiDiConsorzio', 1);
    },

    my_ElencoPersGiuridiche: (form: FormGroup) => {
        schedaCoopSocialiCkHelper(form, 'my_ElencoPersGiuridiche', 'my_CkAmmessiSociFinanz', 1);
    },

    my_DescEntiConvenzionati: (form: FormGroup) => {
        const schedaGroup = getSchedaCoopSocialiGroup(form);
        const control = getControl(schedaGroup, 'my_DescEntiConvenzionati');
        const my_NumConvEnti = getControl(schedaGroup, 'my_NumConvEnti').value;
        const my_ScSocObbl: string = getControl(schedaGroup, 'my_ScSocObbl').value;
        const errors = control.errors ?? {};

        addRemoveError(isNilOrEmpty(control.value) && toNum(my_NumConvEnti) > 0 && my_ScSocObbl === '1', errors, 'my_DescEntiConvenzionati', { status: REQUIRED_STATUS });

        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_MotivoAssenzaSottocategoriaSoc: (form: FormGroup) => {
        schedaCoopSocialiCkHelper(form, 'my_MotivoAssenzaSottocategoriaSoc', 'my_SottocategoriaSezEnteSociale', 1);
    },

    my_MotiviSociSvantagg: (form: FormGroup) => {
        schedaCoopSocialiCkHelper(form, 'my_MotiviSociSvantagg', 'my_CkSociSvantagg', 0);
    },

    my_ckCoopSvolgeCommEqSolid: (form: FormGroup) => {
        schedaCoopSocialiCkHelper(form, 'my_ckCoopSvolgeCommEqSolid', 'my_CkServSocioSanit', 1);
    },

    my_ckAttSocEduPrevCommEqSolid: (form: FormGroup) => {
        commEqSolidHelper(form, 'my_ckAttSocEduPrevCommEqSolid');
    },

    my_ckCommEqSolidStrumAttSocEdu: (form: FormGroup) => {
        commEqSolidHelper(form, 'my_ckCommEqSolidStrumAttSocEdu');
    },

    my_ckCommEqSolidDocumStrumAtt: (form: FormGroup) => {
        commEqSolidHelper(form, 'my_ckCommEqSolidDocumStrumAtt');
    },

    my_ConvEnte: (form: FormGroup) => {
        enteConvHelper(form, 'my_ConvEnte');
    },

    my_ConvOggetto: (form: FormGroup) => {
        enteConvHelper(form, 'my_ConvOggetto');
    },

    my_ImportoConvenzione: (form: FormGroup) => {
        enteConvHelper(form, 'my_ImportoConvenzione');
    },
};

/** True when scheda is active (my_ScSocObbl = 1) and at least one settore B is selected */
const hasCategoria = (form: FormGroup): boolean => {
    const soc = getSchedaCoopSocialiGroup(form);
    const my_ScSocObbl: string = getControl(soc, 'my_ScSocObbl').value;
    return (
        (getControl(soc, 'my_CkAgricolo').value === '1' ||
            getControl(soc, 'my_CkIndustr').value === '1' ||
            getControl(soc, 'my_CkComm').value === '1' ||
            getControl(soc, 'my_CkServiz').value === '1') &&
        my_ScSocObbl === '1'
    );
};

/** Required when hasCategoria AND optional suppCondition */
const categoriaHelper = (form: FormGroup, targetPath: string, suppCondition = true): void => {
    const control = getControl(getSchedaCoopSocialiGroup(form), targetPath);
    const errors = control.errors ?? {};

    addRemoveError(isNilOrEmpty(control.value) && hasCategoria(form) && suppCondition, errors, targetPath, { status: REQUIRED_STATUS });

    control.setErrors(isEqual(errors, {}) ? null : errors);
};

/** Required when my_ckCoopSvolgeCommEqSolid = 1 AND my_CkServSocioSanit = 1 AND my_ScSocObbl = 1 */
const commEqSolidHelper = (form: FormGroup, targetPath: string): void => {
    const my_CkServSocioSanit = getSchedaCoopSocialiControl(form, 'my_CkServSocioSanit').value;
    schedaCoopSocialiCkHelper(form, targetPath, 'my_ckCoopSvolgeCommEqSolid', 1, my_CkServSocioSanit === '1');
};

/** Required for each row in my_ElEntiConv when my_ScSocObbl = 1 */
const enteConvHelper = (form: FormGroup, targetPath: string): void => {
    const my_ElEntiConvFormArray = getFormArray(getSchedaCoopSocialiGroup(form), 'my_EntiConv.my_ElEntiConv');
    const my_ScSocObbl: string = getSchedaCoopSocialiControl(form, 'my_ScSocObbl').value;
    my_ElEntiConvFormArray?.controls?.forEach((group: FormGroup) => {
        const control = getControl(group, targetPath);
        const errors = control.errors ?? {};

        addRemoveError(isNilOrEmpty(control.value) && my_ScSocObbl === '1', errors, targetPath, { status: REQUIRED_STATUS });

        control.setErrors(isEqual(errors, {}) ? null : errors);
    });
};
